<script setup lang="ts">
import { toRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useSupplierSelect } from '@/composables/useSupplierSelect';

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    modelValue?: number | string | null | unknown;
    customerId?: number | string | null | unknown;
    disabled?: boolean;
    placeholder?: string;
    actionLabel?: string;
    pageSize?: number;
    class?: any;
  }>(),
  {
    pageSize: 500,
  }
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: number | string | null): void;
  (e: 'change', supplier: any | null): void;
  (e: 'action'): void;
}>();

function onChangeValue(id: number | string | null) {
  const supplier =
    id != null
      ? suppliers.value.find((s: any) => s.id === id || String(s.id) === String(id))
      : null;
  if (supplier?.is_deleted) return;
  emit('change', supplier ?? null);
}

const customerIdRef = toRef(props, 'customerId');
const modelValueRef = toRef(props, 'modelValue');

const { suppliers, loading, hasMore, selectedSupplierDeleted, fetchSuppliers, loadMore, onSearch } =
  useSupplierSelect({
    customerId: customerIdRef,
    selectedId: modelValueRef,
    pageSize: props.pageSize,
  });

// Fetch suppliers when customerId becomes available or changes
watch(
  customerIdRef,
  val => {
    if (val) fetchSuppliers();
  },
  { immediate: true }
);

defineExpose({
  /** The current suppliers list — can be used to look up supplier data */
  suppliers,
  /** Refetch the full list (e.g. after creating a new supplier) */
  fetchSuppliers,
  /** Whether the currently selected supplier has been deleted */
  selectedSupplierDeleted,
});
</script>

<template>
  <div class="tw-w-full tw-min-w-0">
    <SearchableSelect
      :model-value="modelValue"
      :options="suppliers"
      :loading="loading"
      :remote-search="true"
      :has-more="hasMore"
      :disabled="disabled || !customerId"
      :placeholder="placeholder"
      :action-label="actionLabel"
      :class="['tw-w-full', props.class, selectedSupplierDeleted ? 'tw-border-red-300' : '']"
      value-prop="id"
      label-prop="display_name"
      @update:model-value="emit('update:modelValue', $event)"
      @change="onChangeValue"
      @action="emit('action')"
      @search-change="onSearch"
      @load-more="loadMore"
    />
    <p v-if="selectedSupplierDeleted" class="tw-text-red-500 tw-text-sm tw-mt-1">
      {{ t('suppliers.labels.supplier_archived_warning') }}
    </p>
  </div>
</template>
